import { useCallback } from 'react';
import { isEqual } from 'lodash-es';
import { gql } from '@soundxyz/gql-string';

import type { ReleaseActivityFeedFilterArgs } from '@graphql/reactQuery';
import {
  ReleaseActivityFeedHookDocument,
  ReleaseActivityFeedTypeFilterOption,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import { ReleaseActivityState, useReleaseActivityState } from './store';

export const MAX_RELEASES = 1;
export const INTERSECTION_OBSERVER_ROOT_MARGIN = '-45% -5px';

/*#__PURE__*/ gql(/* GraphQL */ `
  query ReleaseActivityFeedHook(
    $releaseId: UUID!
    $pagination: CursorConnectionArgs!
    $filter: ReleaseActivityFeedFilterArgs
  ) {
    releaseActivityFeed(pagination: $pagination, releaseId: $releaseId, filter: $filter) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          __typename
          id
          date
          user {
            id
          }
          ...ActivityReleaseAction
        }
      }
    }
  }
`);

export function useReleaseActivityFeed({ releaseId }: { releaseId?: string }) {
  const snapshot = useReleaseActivityState();

  const setFilter = useCallback(
    (selection: ReleaseActivityFeedTypeFilterOption[]) => {
      if (isEqual(snapshot.filter.types, selection) || !releaseId) return;
      const newActivityFeedGroupFilter: ReleaseActivityFeedFilterArgs['types'] = selection?.filter(
        option => option !== ReleaseActivityFeedTypeFilterOption.All,
      );
      ReleaseActivityState.filter[releaseId] = { types: newActivityFeedGroupFilter };
    },
    [releaseId, snapshot.filter.types],
  );

  const filter: ReleaseActivityFeedFilterArgs = {
    types:
      releaseId && snapshot.filter[releaseId]?.types?.length
        ? snapshot.filter[releaseId]?.types
        : [ReleaseActivityFeedTypeFilterOption.All], // Fall back to All if no types are selected
  };

  const {
    orderedList: queryData,
    isLoading: loading,
    loadMoreNextPage: loadMore,
    refetch,
    error,
    hasNextPage = false,
  } = useInfiniteQuery(ReleaseActivityFeedHookDocument, {
    getNextPageParam({
      data: {
        releaseActivityFeed: { pageInfo },
      },
    }) {
      return pageInfo.hasNextPage && { after: pageInfo.endCursor };
    },
    filterQueryKey: {
      releaseId,
      filter,
    },
    variables:
      !!releaseId &&
      (({ pageParam }) => {
        const after = pageParam?.after || null;

        return {
          releaseId,
          filter,
          pagination: {
            after,
            first: after == null ? 20 : 10,
          },
        };
      }),
    list({ releaseActivityFeed }) {
      return releaseActivityFeed.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    order: [
      [v => v.date, v => v.id],
      ['desc', 'desc'],
    ],
    staleTime: 0,
  });

  return {
    data: queryData,
    loading,
    loadMore,
    error,
    hasNextPage,
    refetch,
    setFilter,
    filter,
  };
}
